import React from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { rangeFilterSx } from 'common/table/styles/rangeFilterSx';
import { MinMax, TableV3FilterItem } from 'common/table/types/TableV3';
import {
  RangeFilterFields,
  TableV3RangeFilterPopover
} from 'common/table/components/TableV3RangeFilterPopover';
import { tableFilterItemsToMinMax } from 'common/table/utils/minMaxTableFilterItems';
import {
  MAX_YEARS,
  isSupportedYears
} from 'common/table/utils/rangeFilterLimits';

type TableV3NumberRangeFilterButtonProps = {
  unit?: string;
  selectedItems: TableV3FilterItem[];
  isSorted?: boolean;
  onFilterApply: (items: TableV3FilterItem[]) => void;
};

type Comparator = 'between' | 'atLeast' | 'atMost';

type Draft = { comparator: Comparator; min: string; max: string };

const COMPARATORS: { value: Comparator; label: string }[] = [
  { value: 'between', label: 'Between or equal to' },
  { value: 'atLeast', label: 'More than or equal to' },
  { value: 'atMost', label: 'Less than or equal to' }
];

const minMaxToDraft = ({ min, max }: MinMax): Draft => ({
  comparator:
    min !== undefined && max === undefined
      ? 'atLeast'
      : min === undefined && max !== undefined
      ? 'atMost'
      : 'between',
  min: min ?? '',
  max: max ?? ''
});

const tableFilterItemsToDraft = (items: TableV3FilterItem[]) =>
  minMaxToDraft(tableFilterItemsToMinMax(items));

const allowsNegative = (unit?: string) => unit !== 'years';

const isValidMinOrMax = (value: string, unit?: string) =>
  value.trim() !== '' &&
  Number.isFinite(Number(value)) &&
  (allowsNegative(unit) || isSupportedYears(Number(value)));

const draftToMinMax =
  (unit?: string) =>
  ({ comparator, min, max }: Draft): MinMax | null => {
    const showsMin = comparator !== 'atMost';
    const showsMax = comparator !== 'atLeast';
    if (
      (showsMin && !isValidMinOrMax(min, unit)) ||
      (showsMax && !isValidMinOrMax(max, unit))
    )
      return null;
    if (showsMin && showsMax && Number(min) > Number(max))
      return { min: max, max: min };

    return {
      ...(showsMin ? { min } : {}),
      ...(showsMax ? { max } : {})
    };
  };

export const TableV3NumberRangeFilterButton = ({
  unit,
  ...props
}: TableV3NumberRangeFilterButtonProps): React.ReactElement => {
  const renderFields = ({
    draft,
    setDraft,
    onKeyDown
  }: RangeFilterFields<Draft>) => {
    const renderInput = (minOrMax: 'min' | 'max') => (
      <TextField
        type="number"
        size="small"
        value={draft[minOrMax]}
        onChange={(event) => {
          const { value } = event.target;
          setDraft((current) => ({ ...current, [minOrMax]: value }));
        }}
        onKeyDown={onKeyDown}
        autoFocus={minOrMax === 'min' || draft.comparator === 'atMost'}
        inputProps={{
          ...(allowsNegative(unit) ? {} : { min: 0, max: MAX_YEARS }),
          step: 'any',
          'aria-label': minOrMax === 'min' ? 'Minimum' : 'Maximum'
        }}
        sx={rangeFilterSx.input}
      />
    );

    return (
      <Box sx={rangeFilterSx.row}>
        {draft.comparator !== 'atMost' && renderInput('min')}
        {draft.comparator === 'between' && <Typography>and</Typography>}
        {draft.comparator !== 'atLeast' && renderInput('max')}
        {unit && <Typography>{unit}</Typography>}
      </Box>
    );
  };

  return (
    <TableV3RangeFilterPopover
      {...props}
      comparators={COMPARATORS}
      tableFilterItemsToDraft={tableFilterItemsToDraft}
      draftToMinMax={draftToMinMax(unit)}
      renderFields={renderFields}
    />
  );
};
